import { AnchorTag, FormattedText, SpanTag } from "@tm/static";
import { theme } from "@tm/utility/global";
import LandingButton from "../../../../../components/LandingButton/LandingButton";
import {
  Badge,
  BenefitDescText,
  BenefitIcon,
  BenefitRow,
  BenefitsSection,
  BenefitTitleText,
  CardContainer,
  CardHeader,
  CardInner,
  CardWrapper,
  FooterContent,
  FooterIcon,
  FooterWrapper,
  HeaderPersuasionIcon,
  PriceRow,
  SavingsTag,
  SavingsTagIcon,
  UnitText,
} from "./Card.styles";
import { IPlanCardData, MyCashState } from "./types";

interface CardProps extends IPlanCardData {
  myCashState?: MyCashState;
  onBuyNow?: (
    planId: string,
    productId?: number,
    isRecommended?: boolean
  ) => void;
  onViewBenefits?: (planId: string) => void;
}

const Card = ({
  planId,
  productId,
  badge,
  title,
  prices,
  unit,
  tagIcon,
  headerPersuation,
  previewBenefits,
  cta,
  footerPersuation,
  myCashState = "unApplied",
  onBuyNow,
  onViewBenefits,
}: CardProps) => {
  const isMyCashApplied = myCashState === "applied";
  const isRecommended = !!badge?.text;

  const displayPrice = isMyCashApplied
    ? prices?.mycashAppliedPrice
    : prices?.discountedPrice;

  const persuasion = isMyCashApplied
    ? headerPersuation?.mycashApplied
    : headerPersuation?.generic;

  const handleBuyNow = () => {
    try {
      if (planId) onBuyNow?.(planId, productId, isRecommended);
    } catch (error) {
      console.error("Plan card buy action failed", error);
    }
  };

  const handleViewBenefits = () => {
    try {
      if (planId) onViewBenefits?.(planId);
    } catch (error) {
      console.error("Plan card benefits action failed", error);
    }
  };

  return (
    <CardWrapper>
      {isRecommended && (
        <Badge $gradient={badge?.styles?.bgColorGradient}>{badge?.text}</Badge>
      )}
      <CardContainer
        $isRecommended={isRecommended}
        $borderColor={badge?.styles?.bgColorGradient?.[0]}
      >
        <CardInner>
          <CardHeader>
            {title && (
              <SpanTag
                fontSize="sm"
                fontWeight="bold"
                color={theme.prussianBlue}
              >
                {title}
              </SpanTag>
            )}
            <PriceRow>
              {prices?.originalPrice && (
                <SpanTag
                  fontSize="xs"
                  color={theme.greyText6}
                  style={{
                    textDecoration: "line-through",
                    whiteSpace: "nowrap",
                  }}
                >
                  {prices.originalPrice}
                </SpanTag>
              )}
              {displayPrice && (
                <SpanTag fontSize="large" fontWeight="black">
                  {displayPrice}
                </SpanTag>
              )}
              {unit && (
                <UnitText>
                  <SpanTag fontSize="xs" color={theme.greyText6}>
                    {unit}
                  </SpanTag>
                </UnitText>
              )}
            </PriceRow>
            {persuasion?.text && (
              <SavingsTag $isMyCash={isMyCashApplied}>
                {isMyCashApplied && persuasion?.icon && (
                  <SavingsTagIcon src={persuasion.icon} alt="" />
                )}
                {persuasion.text}
              </SavingsTag>
            )}
            {tagIcon && <HeaderPersuasionIcon src={tagIcon} alt="" />}
            {!isMyCashApplied && !tagIcon && persuasion?.icon && (
              <HeaderPersuasionIcon src={persuasion.icon} alt="" />
            )}
          </CardHeader>

          {previewBenefits && previewBenefits.length > 0 && (
            <BenefitsSection>
              {previewBenefits.slice(0, 3).map((benefit, idx) => {
                if (!benefit) return null;
                return (
                  <BenefitRow key={idx}>
                    {benefit.icon && <BenefitIcon src={benefit.icon} alt="" />}
                    <BenefitTitleText>
                      <SpanTag fontSize="xs" color={theme.greyText}>
                        {benefit.title}
                      </SpanTag>
                    </BenefitTitleText>
                    {benefit.desc && (
                      <BenefitDescText>
                        <SpanTag
                          fontSize="xs"
                          fontWeight="bold"
                          color={theme.greyText}
                        >
                          {benefit.desc}
                        </SpanTag>
                      </BenefitDescText>
                    )}
                  </BenefitRow>
                );
              })}
              {cta?.benefitsText && (
                <AnchorTag
                  fontSize="xs"
                  fontWeight="bold"
                  handleClick={handleViewBenefits}
                >
                  {cta.benefitsText}
                </AnchorTag>
              )}
            </BenefitsSection>
          )}

          <LandingButton
            customTitle="BUY NOW"
            customWidth="calc(100% - 32px)"
            customHeight="32px"
            fontSize="12px"
            onClick={handleBuyNow}
          />
        </CardInner>
      </CardContainer>

      {footerPersuation?.title && (
        <FooterWrapper>
          {footerPersuation.icon && (
            <FooterIcon src={footerPersuation.icon} alt="" />
          )}
          <FooterContent>
            <SpanTag fontSize="xs" color={theme.blackText}>
              <FormattedText value={footerPersuation.title} />
            </SpanTag>
          </FooterContent>
        </FooterWrapper>
      )}
    </CardWrapper>
  );
};

export default Card;
